import { useState, useEffect } from "react";

// types
import { BusinessSize } from "types/filters-store.types";

// components
import {
  FilterFlyout,
  Radio,
  RoundedButton,
} from "components/LibraryComponents/Components";
import { TextInput } from "@trussworks/react-uswds";
import { PeopleIcon } from "components/icons";

// constants
import {
  FilterKeys,
  FilterTitles,
  RelationOperatorType,
} from "constants/filters";

// store
import useFiltersStore from "store/useFiltersStoreNew";

// hooks
import useDebounce from "hooks/useDebounce";

// styles
import styles from "./BusinessSizeFilterButton.module.scss";

export default function BusinessSizeFilterButton() {
  const { businessSize, setFilter } = useFiltersStore((state) => ({
    businessSize: state.filters.businessSize,
    setFilter: state.setFilter,
  }));

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>(businessSize.numberOfEmployees);
  // This state only control if filter is open,
  // and has more than one value apply max-width to filter button.
  const [isHalt, setIsHalt] = useState<boolean>(true);

  const debouncedValue = useDebounce(value, 350);

  const selectedValue = getSelectedValue(businessSize);
  const isDisabled = selectedValue.length === 0;

  // check if filter is open and has more than one value
  useEffect(() => {
    if (selectedValue.length === 0) {
      if (!isHalt) setIsHalt(true);
    }
  }, [selectedValue]);

  // set number of employees by debounced value
  useEffect(() => {
    if (!debouncedValue) {
      return;
    }

    setFilter(FilterKeys.BUSINESS_SIZE, {
      ...businessSize,
      numberOfEmployees: debouncedValue,
    });
  }, [debouncedValue]);

  // set value from filter store on mount, if value is not empty
  useEffect(() => {
    if (value !== "" && businessSize.numberOfEmployees === "") {
      setValue("");
    }
  }, [businessSize.numberOfEmployees]);

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;

    setFilter(FilterKeys.BUSINESS_SIZE, {
      ...businessSize,
      relationOperator: id,
    });
  };

  const handleNumberOfEmployeesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    if (value && Number(value) <= 0) {
      return;
    }

    setValue(value);
  };

  const handleCallback = (open: boolean) => {
    if (!open && businessSize.numberOfEmployees !== "") {
      setIsHalt(false);
    }
  };

  const handleClear = () => {
    setFilter(FilterKeys.BUSINESS_SIZE, {
      relationOperator: RelationOperatorType.AT_LEAST,
      numberOfEmployees: "",
    });

    setValue("");
  };

  return (
    <FilterFlyout
      id={FilterKeys.BUSINESS_SIZE}
      filterName={FilterTitles[FilterKeys.BUSINESS_SIZE]}
      title={FilterTitles[FilterKeys.BUSINESS_SIZE]}
      FilterIcon={<PeopleIcon aria-label="Business Size Filter Icon" />}
      options={selectedValue}
      isOpen={isOpen}
      isHalt={isHalt}
      setIsOpen={setIsOpen}
      callback={handleCallback}
    >
      <div className={styles["business-size-filter-content"]}>
        <div className={styles["radio-group"]}>
          <Radio
            id={RelationOperatorType.AT_LEAST}
            name={RelationOperatorType.AT_LEAST}
            value={RelationOperatorType.AT_LEAST}
            label="At least"
            checked={
              businessSize.relationOperator === RelationOperatorType.AT_LEAST
            }
            onChange={handleRadioChange}
          />
          <Radio
            id={RelationOperatorType.NO_MORE}
            name={RelationOperatorType.NO_MORE}
            value={RelationOperatorType.NO_MORE}
            label="No more than"
            checked={
              businessSize.relationOperator === RelationOperatorType.NO_MORE
            }
            onChange={handleRadioChange}
          />
        </div>

        <div className={styles["input-group"]}>
          <label htmlFor="numberOfEmployees">Number of employees</label>
          <div className={styles["input-wrapper"]}>
            <TextInput
              id="numberOfEmployees"
              name="numberOfEmployees"
              type="number"
              placeholder="Type a number"
              // value={businessSize.numberOfEmployees}
              value={value}
              onChange={handleNumberOfEmployeesChange}
            />
          </div>
        </div>

        <div className={styles["clear-filter"]}>
          <RoundedButton
            variant="ghost"
            onClick={handleClear}
            disabled={isDisabled}
          >
            Clear filter selections
          </RoundedButton>
        </div>
      </div>
    </FilterFlyout>
  );
}

function getSelectedValue(businessSize: BusinessSize) {
  if (businessSize.numberOfEmployees !== "") {
    const numOfEmps = Number(businessSize.numberOfEmployees).toLocaleString(
      "en-US"
    );

    if (businessSize.relationOperator === RelationOperatorType.AT_LEAST) {
      return [`≥ ${numOfEmps}`];
    } else {
      return [`≤ ${numOfEmps}`];
    }
  }

  return [];
}
